import React, { useState, useEffect, useRef } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import gallerAddIcon from '../../../../assets/icons/galleryadd.svg';
import deleteIcon from '../../../../assets/icons/deleteRound.svg';
import { t } from 'i18next';
import { saveToDB, getFromDB } from '../../../../utils/indexedDBService';

const STORE_NAME_IMAGES = "savedImages";
const STORE_NAME_FILES = "savedImageFiles";

const ImageSelector = ({ onImagesChange, initialImages = [], initialFiles = [] }) => {
    const maxImages = 4;
    const [images, setImages] = useState(Array(maxImages).fill(null));
    const [imageFiles, setImageFiles] = useState(Array(maxImages).fill(null));
    const [isMobile, setIsMobile] = useState(false);
    const hasInitializedRef = useRef(false);
    const prevInitialImagesStrRef = useRef('');
    const prevInitialFilesStrRef = useRef('');

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        // Serialize current initial data for comparison
        const currImagesStr = JSON.stringify(initialImages);
        const currFilesStr = JSON.stringify(initialFiles);
        
        // Skip if already initialized and data hasn't changed
        if (hasInitializedRef.current) {
            if (prevInitialImagesStrRef.current === currImagesStr && prevInitialFilesStrRef.current === currFilesStr) {
                return; // Data hasn't changed, skip update
            }
        }

        if (initialImages.length || initialFiles.length) {
            const paddedImages = [...initialImages, ...Array(maxImages - initialImages.length).fill(null)];
            const paddedFiles = [...initialFiles, ...Array(maxImages - initialFiles.length).fill(null)];
            setImages(paddedImages);
            setImageFiles(paddedFiles);
            prevInitialImagesStrRef.current = currImagesStr;
            prevInitialFilesStrRef.current = currFilesStr;
            hasInitializedRef.current = true;
        } else if (!hasInitializedRef.current) {
            // Only fetch from DB on first mount if no initial data
            const fetchImages = async () => {
                try {
                    const savedImages = await getFromDB(STORE_NAME_IMAGES) || [];
                    const savedFiles = await getFromDB(STORE_NAME_FILES) || [];
                    setImages([...savedImages, ...Array(maxImages - savedImages.length).fill(null)]);
                    setImageFiles([...savedFiles, ...Array(maxImages - savedFiles.length).fill(null)]);
                    prevInitialImagesStrRef.current = '';
                    prevInitialFilesStrRef.current = '';
                    hasInitializedRef.current = true;
                } catch (error) {
                    console.error("Error loading images from DB:", error);
                }
            };
            fetchImages();
        }
    }, [initialImages, initialFiles]);

    const convertToJpeg = (file, callback) => {
        const img = new Image();
        img.src = URL.createObjectURL(file);
        img.onload = () => {
            const canvas = document.createElement("canvas");
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext("2d");
            ctx.drawImage(img, 0, 0);
            canvas.toBlob((blob) => {
                callback(blob);
            }, "image/jpeg", 0.9);
        };
    };

    const handleImageUpload = (e) => {
        const files = Array.from(e.target.files);
        if (files.length > maxImages) {
            alert(t("maxImagesError"));
            return;
        }

        const newImages = [...images];
        const newImageFiles = [...imageFiles];

        files.forEach((file, i) => {
            convertToJpeg(file, (jpegBlob) => {
                const reader = new FileReader();
                reader.onload = () => {
                    const idx = newImages.findIndex((img) => img === null);
                    if (idx !== -1) {
                        newImages[idx] = reader.result;
                        newImageFiles[idx] = new File([jpegBlob], `${file.name.split('.')[0]}.jpg`, { type: "image/jpeg" });
                    }

                    if (i === files.length - 1) {
                        setImages([...newImages]);
                        setImageFiles([...newImageFiles]);
                        saveToDB(STORE_NAME_IMAGES, newImages.filter(Boolean));
                        saveToDB(STORE_NAME_FILES, newImageFiles.filter(Boolean));
                        onImagesChange(newImageFiles.filter(Boolean));
                    }
                };
                reader.readAsDataURL(jpegBlob);
            });
        });
    };


    const handleRemoveImage = (index) => {
        const newImages = [...images];
        const newImageFiles = [...imageFiles];

        newImages[index] = null;
        newImageFiles[index] = null;

        setImages([...newImages]);
        setImageFiles([...newImageFiles]);
        saveToDB(STORE_NAME_IMAGES, newImages.filter(Boolean));
        saveToDB(STORE_NAME_FILES, newImageFiles.filter(Boolean));

        onImagesChange(newImageFiles.filter(Boolean));
    };

    const onDragEnd = (result) => {
        if (!result.destination) return;

        const newImages = [...images];
        const newImageFiles = [...imageFiles];

        const [movedImage] = newImages.splice(result.source.index, 1);
        const [movedFile] = newImageFiles.splice(result.source.index, 1);

        newImages.splice(result.destination.index, 0, movedImage);
        newImageFiles.splice(result.destination.index, 0, movedFile);

        setImages(newImages);
        setImageFiles(newImageFiles);
        onImagesChange(newImageFiles.filter((f) => f));
    };

    const EmptySlot = ({ index, isMain }) => {
        return (
            <label className="flex flex-col gap-2 items-center justify-center w-full h-full cursor-pointer rounded-xl border-2 border-dashed border-[#4CAF50]/40 bg-[#F0F7F1] hover:bg-[#E3F2E5] hover:border-[#4CAF50] transition-all duration-200 group/slot">
                <input type="file" accept="image/*" multiple onChange={handleImageUpload} className="hidden" />
                <div className={`flex items-center justify-center rounded-full bg-white shadow-sm ${isMain ? 'w-12 h-12' : 'w-9 h-9'} group-hover/slot:scale-110 transition-transform duration-200`}>
                    <img src={gallerAddIcon} alt="add" className={isMain ? 'w-6 h-6' : 'w-5 h-5'} />
                </div>
                {isMain && <span className="text-sm font-medium text-[#4CAF50]">{t('add')}</span>}
            </label>
        );
    };

    return (
        <div className="w-full my-5">
            {isMobile ? (
                <label className="flex items-center justify-center w-full h-[140px] bg-[#F0F7F1] rounded-xl cursor-pointer relative overflow-hidden border-2 border-dashed border-[#4CAF50]/40">
                    <input type="file" accept="image/*" multiple onChange={handleImageUpload} hidden />
                    <div className="flex flex-col items-center justify-center w-full h-full">
                        {images.some(Boolean) ? (
                            <div className="flex flex-wrap gap-2 justify-center items-center p-2 w-full">
                                {images.map((img, index) =>
                                    img ? (
                                        <div key={index} className="relative w-16 h-16 rounded-lg overflow-hidden shadow-sm">
                                            <img src={img} alt={`img-${index}`} className="w-full h-full object-cover" />
                                            <button
                                                className="absolute inset-0 flex items-center justify-center bg-black/30 border-none cursor-pointer"
                                                onClick={(e) => { e.preventDefault(); handleRemoveImage(index); }}
                                            >
                                                <img src={deleteIcon} alt="delete" className="w-5 h-5" />
                                            </button>
                                        </div>
                                    ) : null
                                )}
                            </div>
                        ) : (
                            <div className="flex flex-col gap-2 items-center text-[#4CAF50]">
                                <img src={gallerAddIcon} alt="add" className="w-8 h-8" />
                                <span className="text-sm font-medium">{t('add')}</span>
                            </div>
                        )}
                    </div>
                </label>
            ) : (
                <>
                    <div className="flex flex-row justify-between items-start mb-4">
                        <div>
                            <h3 className="text-[15px] font-bold text-gray-800 m-0 mb-1">{t('announcementPhoto')}</h3>
                            <p className="text-sm text-gray-500 font-normal m-0">{t('announcementPhotos')}</p>
                        </div>
                        <span className="text-xs text-gray-400 bg-gray-100 px-3 py-1 rounded-full">{t("maxPhotosCount", { count: 4 })}</span>
                    </div>

                    <DragDropContext onDragEnd={onDragEnd}>
                        <Droppable droppableId="images" direction="horizontal">
                            {(provided) => {
                                const filledCount = images.filter(Boolean).length;
                                const visibleCount = Math.min(filledCount + 1, maxImages);
                                return (
                                    <div
                                        className="flex flex-row gap-3"
                                        style={{ height: '180px' }}
                                        {...provided.droppableProps}
                                        ref={provided.innerRef}
                                    >
                                        {images.map((img, index) => (
                                            <Draggable key={index} draggableId={String(index)} index={index}>
                                                {(provided, snapshot) => (
                                                    <div
                                                        ref={provided.innerRef}
                                                        {...provided.draggableProps}
                                                        {...provided.dragHandleProps}
                                                        style={{
                                                            ...provided.draggableProps.style,
                                                            display: index < visibleCount ? undefined : 'none',
                                                            flex: index === 0 ? '2 1 0%' : '1 1 0%',
                                                            minWidth: 0,
                                                        }}
                                                        className={`group relative rounded-xl overflow-hidden bg-[#F0F7F1] h-full ${snapshot.isDragging ? 'shadow-2xl scale-105 z-50' : ''} transition-all duration-200`}
                                                    >
                                                        {img ? (
                                                            <>
                                                                <img src={img} alt={`${index}`} className="w-full h-full object-cover" />
                                                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-200" />
                                                                {index === 0 && (
                                                                    <span className="absolute top-2 left-2 bg-black/60 backdrop-blur-sm text-white text-xs px-2 py-1 rounded-full font-medium">
                                                                        {t('main') || 'Негізгі'}
                                                                    </span>
                                                                )}
                                                                <button
                                                                    onClick={() => handleRemoveImage(index)}
                                                                    className="absolute top-2 right-2 w-8 h-8 rounded-full bg-white/90 backdrop-blur-sm shadow-md flex items-center justify-center opacity-0 group-hover:opacity-100 hover:bg-red-50 transition-all duration-200"
                                                                >
                                                                    <img src={deleteIcon} alt="delete" className="w-4 h-4" />
                                                                </button>
                                                            </>
                                                        ) : (
                                                            <EmptySlot index={index} isMain={index === 0} />
                                                        )}
                                                    </div>
                                                )}
                                            </Draggable>
                                        ))}
                                        {provided.placeholder}
                                    </div>
                                );
                            }}
                        </Droppable>
                    </DragDropContext>
                </>
            )}
        </div>
    );
};

export default ImageSelector;